/**
 * Pipeline Monitoring Page
 * Tracks file ingestion progress end-to-end:
 *   file_metadata → raw Kafka → fhid service → MongoDB → CDC → S3
 */

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  RefreshCw,
  Workflow,
  CheckCircle,
  XCircle,
  Loader2,
  ChevronDown,
  ChevronRight,
  FileText,
  AlertCircle,
  Clock,
  FlaskConical,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { LoadingState, ErrorState } from '@/components/ui/loading-state'
import { Select } from '@/components/ui/select'
import { cn, formatDate, formatNumber } from '@/lib/utils'
import { useAccount } from '@/contexts/AccountContext'
import { getPipelineFiles } from '../queries/pipelineMonitoring'
import { PipelineStageBar } from '../components/PipelineStageBar'
import { PipelineStageDetail } from '../components/PipelineStageDetail'
import type { FileIngestionStatus, PipelineMonitorResponse, PipelineStageStatus } from '../types'

// ─── Demo data ───────────────────────────────────────────────────────────────

function makeStages(config: Array<[string, Partial<PipelineStageStatus>]>): PipelineStageStatus[] {
  return config.map(([stage, overrides]) => ({
    stage,
    status: 'done',
    expected: 50000,
    actual: 50000,
    lag: 0,
    pctComplete: 100,
    lastActivity: null,
    details: null,
    ...overrides,
  }))
}

const DEMO_RESPONSE: PipelineMonitorResponse = {
  accId: 7943,
  totalFiles: 4,
  filesInProgress: 1,
  filesDone: 2,
  filesWithErrors: 1,
  asOf: new Date().toISOString(),
  files: [
    {
      fileId: 'demo-001',
      accId: 7943,
      category: 'entity',
      entityType: 'policy',
      filePath: 's3://fh-ingestion/acc_7943/entity/policy_upload_20260328_01.csv',
      fileStatus: 'DONE',
      totalRowCount: 50000,
      errorCount: 0,
      errorMsg: null,
      createdAt: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
      processingStartTime: new Date(Date.now() - 2.9 * 60 * 60 * 1000).toISOString(),
      processingEndTime: new Date(Date.now() - 2.5 * 60 * 60 * 1000).toISOString(),
      overallStatus: 'done',
      bottleneck: null,
      stages: makeStages([
        ['file_registered', { details: { file_status: 'DONE', total_row_count: 50000 } }],
        ['raw_published', { details: { topic: 'entity-raw', published_since_upload: 50000 } }],
        ['fhid_assigned', { details: { group: 'entity-raw-group', topic: 'entity-raw', lag: 0 } }],
        ['mongo_upserted', { details: { collection: 'custom_policy_7943', upserted_count: 50000 } }],
        ['cdc_emitted', { details: { group: 'connect-s3-sink-custom-entity', lag: 0 } }],
      ]),
    },
    {
      fileId: 'demo-002',
      accId: 7943,
      category: 'customer',
      entityType: null,
      filePath: 's3://fh-ingestion/acc_7943/customer/customer_update_20260328_02.csv',
      fileStatus: 'DONE',
      totalRowCount: 120000,
      errorCount: 0,
      errorMsg: null,
      createdAt: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
      processingStartTime: new Date(Date.now() - 4.8 * 60 * 60 * 1000).toISOString(),
      processingEndTime: new Date(Date.now() - 4.2 * 60 * 60 * 1000).toISOString(),
      overallStatus: 'done',
      bottleneck: null,
      stages: makeStages([
        ['file_registered', { expected: 120000, actual: 120000, details: { file_status: 'DONE', total_row_count: 120000 } }],
        ['raw_published', { expected: 120000, actual: 120000, details: { topic: 'customer-raw', published_since_upload: 120000 } }],
        ['fhid_assigned', { expected: 120000, actual: 120000, details: { group: 'customer-raw-group', lag: 0 } }],
        ['mongo_upserted', { expected: 120000, actual: 120000, details: { collection: 'customer_7943', upserted_count: 120000 } }],
        ['cdc_emitted', { expected: 120000, actual: 120000, details: { group: 'connect-s3-sink-customer', lag: 0 } }],
      ]),
    },
    {
      fileId: 'demo-003',
      accId: 7943,
      category: 'entity',
      entityType: 'member',
      filePath: 's3://fh-ingestion/acc_7943/entity/member_upload_20260328_03.csv',
      fileStatus: 'STARTED',
      totalRowCount: 80000,
      errorCount: 0,
      errorMsg: null,
      createdAt: new Date(Date.now() - 45 * 60 * 1000).toISOString(),
      processingStartTime: new Date(Date.now() - 40 * 60 * 1000).toISOString(),
      processingEndTime: null,
      overallStatus: 'in_progress',
      bottleneck: 'mongo_upserted',
      stages: makeStages([
        ['file_registered', { expected: 80000, actual: 80000, details: { file_status: 'STARTED', total_row_count: 80000 } }],
        ['raw_published', { expected: 80000, actual: 80000, details: { topic: 'entity-raw', published_since_upload: 80000 } }],
        ['fhid_assigned', { expected: 80000, actual: 80000, details: { group: 'entity-raw-group', lag: 0 } }],
        ['mongo_upserted', {
          expected: 80000, actual: 52400, lag: 27600, pctComplete: 65,
          status: 'in_progress',
          lastActivity: new Date(Date.now() - 2 * 60 * 1000).toISOString(),
          details: { collection: 'custom_member_7943', upserted_count: 52400, since: new Date(Date.now() - 45 * 60 * 1000).toISOString() },
        }],
        ['cdc_emitted', {
          expected: 80000, actual: 0, lag: null, pctComplete: 0,
          status: 'pending',
          details: null,
        }],
      ]),
    },
    {
      fileId: 'demo-004',
      accId: 7943,
      category: 'entity',
      entityType: 'asset',
      filePath: 's3://fh-ingestion/acc_7943/entity/asset_upload_20260328_04.csv',
      fileStatus: 'VALIDATION_ERROR',
      totalRowCount: 30000,
      errorCount: 1247,
      errorMsg: 'Schema validation failed: 1247 rows have invalid vehicle_registration format. Expected pattern: [A-Z]{2}[0-9]{2}[A-Z]{2}[0-9]{4}',
      createdAt: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
      processingStartTime: new Date(Date.now() - 28 * 60 * 1000).toISOString(),
      processingEndTime: new Date(Date.now() - 27 * 60 * 1000).toISOString(),
      overallStatus: 'error',
      bottleneck: null,
      stages: makeStages([
        ['file_registered', {
          expected: 30000, actual: 0, lag: null, pctComplete: null,
          status: 'error',
          details: { file_status: 'VALIDATION_ERROR', total_row_count: 30000, error_count: 1247 },
        }],
        ['raw_published', { expected: 30000, actual: 0, lag: null, pctComplete: 0, status: 'pending', details: null }],
        ['fhid_assigned', { expected: 30000, actual: 0, lag: null, pctComplete: 0, status: 'pending', details: null }],
        ['mongo_upserted', { expected: 30000, actual: 0, lag: null, pctComplete: 0, status: 'pending', details: null }],
        ['cdc_emitted', { expected: 30000, actual: 0, lag: null, pctComplete: 0, status: 'pending', details: null }],
      ]),
    },
  ],
}

// ─── Status config ───────────────────────────────────────────────────────────

const OVERALL_STATUS_CONFIG = {
  done:        { label: 'Done',        icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50 border-green-200'  },
  in_progress: { label: 'In Progress', icon: Loader2,     color: 'text-blue-600',  bg: 'bg-blue-50 border-blue-200'   },
  stalled:     { label: 'Stalled',     icon: Clock,       color: 'text-amber-600', bg: 'bg-amber-50 border-amber-200' },
  error:       { label: 'Error',       icon: XCircle,     color: 'text-red-600',   bg: 'bg-red-50 border-red-200'     },
  unknown:     { label: 'Unknown',     icon: AlertCircle, color: 'text-gray-400',  bg: 'bg-gray-50 border-gray-200'   },
}

function OverallStatusBadge({ status }: { status: FileIngestionStatus['overallStatus'] }) {
  const cfg = OVERALL_STATUS_CONFIG[status] ?? OVERALL_STATUS_CONFIG.unknown
  const Icon = cfg.icon
  return (
    <span className={cn('inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border', cfg.bg, cfg.color)}>
      <Icon className={cn('h-3 w-3', status === 'in_progress' && 'animate-spin')} />
      {cfg.label}
    </span>
  )
}

function FileStatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    DONE:             'bg-green-100 text-green-700',
    STARTED:          'bg-blue-100 text-blue-700',
    NOT_STARTED:      'bg-gray-100 text-gray-500',
    VALIDATION_DONE:  'bg-indigo-100 text-indigo-700',
    ERROR:            'bg-red-100 text-red-700',
    VALIDATION_ERROR: 'bg-red-100 text-red-700',
  }
  return (
    <span className={cn('text-xs font-medium px-2 py-0.5 rounded-full', colors[status] ?? 'bg-gray-100 text-gray-500')}>
      {status.replace(/_/g, ' ')}
    </span>
  )
}

function CategoryBadge({ category, entityType }: { category: string; entityType: string | null }) {
  const colors: Record<string, string> = {
    customer:    'bg-violet-100 text-violet-700',
    entity:      'bg-cyan-100 text-cyan-700',
    system:      'bg-orange-100 text-orange-700',
    transaction: 'bg-teal-100 text-teal-700',
    event:       'bg-pink-100 text-pink-700',
  }
  const label = entityType ? `${category}/${entityType}` : category
  return (
    <span className={cn('text-xs font-medium px-2 py-0.5 rounded-full', colors[category] ?? 'bg-gray-100 text-gray-500')}>
      {label}
    </span>
  )
}

function shortFileName(filePath: string | null) {
  if (!filePath) return '—'
  const parts = filePath.split(/[/\\]/)
  return parts[parts.length - 1] || filePath
}

// ─── Filter options ──────────────────────────────────────────────────────────

const STATUS_OPTIONS = [
  { value: 'ALL',              label: 'All Statuses'     },
  { value: 'NOT_STARTED',      label: 'Not Started'      },
  { value: 'STARTED',          label: 'Started'          },
  { value: 'VALIDATION_DONE',  label: 'Validation Done'  },
  { value: 'DONE',             label: 'Done'             },
  { value: 'ERROR',            label: 'Error'            },
  { value: 'VALIDATION_ERROR', label: 'Validation Error' },
]

const LIMIT_OPTIONS = [
  { value: '10', label: 'Last 10 files' },
  { value: '20', label: 'Last 20 files' },
  { value: '50', label: 'Last 50 files' },
]

// ─── File row ────────────────────────────────────────────────────────────────

function FileRow({ file }: { file: FileIngestionStatus }) {
  const [expanded, setExpanded] = useState(false)

  return (
    <>
      <tr
        className="hover:bg-gray-50 cursor-pointer"
        onClick={() => setExpanded((e) => !e)}
      >
        <td className="pl-4 pr-2 py-3 w-8">
          {expanded
            ? <ChevronDown className="h-4 w-4 text-gray-400" />
            : <ChevronRight className="h-4 w-4 text-gray-400" />}
        </td>

        <td className="px-3 py-3 max-w-[220px]">
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-gray-400 flex-shrink-0" />
            <span className="text-sm font-medium text-gray-800 truncate" title={file.filePath ?? undefined}>
              {shortFileName(file.filePath)}
            </span>
          </div>
        </td>

        <td className="px-3 py-3 whitespace-nowrap">
          <CategoryBadge category={file.category} entityType={file.entityType} />
        </td>

        <td className="px-3 py-3 whitespace-nowrap">
          <FileStatusBadge status={file.fileStatus} />
        </td>

        <td className="px-3 py-3 text-right whitespace-nowrap">
          <span className="text-sm text-gray-700 font-mono">{formatNumber(file.totalRowCount)}</span>
          {file.errorCount > 0 && (
            <span className="ml-2 text-xs text-red-500 font-mono">({formatNumber(file.errorCount)} err)</span>
          )}
        </td>

        <td className="px-3 py-3 text-xs text-gray-400 whitespace-nowrap">
          {file.createdAt ? formatDate(file.createdAt) : '—'}
        </td>

        <td className="px-3 py-3">
          <PipelineStageBar stages={file.stages} />
        </td>

        <td className="px-3 py-3 whitespace-nowrap">
          <OverallStatusBadge status={file.overallStatus} />
          {file.bottleneck && (
            <div className="text-[10px] text-amber-600 mt-0.5">
              blocked at: {file.bottleneck.replace(/_/g, ' ')}
            </div>
          )}
        </td>
      </tr>

      {expanded && (
        <tr>
          <td colSpan={8} className="p-0">
            <PipelineStageDetail stages={file.stages} bottleneck={file.bottleneck} />
            {file.errorMsg && (
              <div className="px-6 pb-3 text-xs text-red-600 bg-red-50 border-t border-red-100">
                <span className="font-semibold">Error: </span>{file.errorMsg}
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  )
}

// ─── Main page ───────────────────────────────────────────────────────────────

export function PipelineMonitoringPage() {
  const { selectedAccountId } = useAccount()
  const [statusFilter, setStatusFilter] = useState('ALL')
  const [limitFilter, setLimitFilter] = useState('20')
  const [demoMode, setDemoMode] = useState(false)

  const accId = selectedAccountId && selectedAccountId !== 'ALL'
    ? parseInt(selectedAccountId, 10)
    : null

  const {
    data: liveData,
    isLoading,
    error,
    refetch,
    isFetching,
    dataUpdatedAt,
  } = useQuery<PipelineMonitorResponse>({
    queryKey: ['pipeline-files', accId, limitFilter, statusFilter],
    queryFn: () => getPipelineFiles({
      accId: accId!,
      limit: parseInt(limitFilter, 10),
      status: statusFilter !== 'ALL' ? statusFilter : undefined,
    }),
    enabled: accId != null && !demoMode,
    refetchInterval: 30000,
  })

  const data = demoMode ? DEMO_RESPONSE : liveData
  const files = data?.files ?? []

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Workflow className="h-6 w-6 text-indigo-600" />
            Pipeline Monitor
          </h1>
          <p className="text-gray-500 mt-1">
            End-to-end ingestion status — file metadata → raw Kafka → FHID → MongoDB → CDC → S3.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {!demoMode && dataUpdatedAt > 0 && (
            <span className="text-xs text-gray-400">
              Updated {formatDate(new Date(dataUpdatedAt).toISOString())} · auto-refreshes every 30s
            </span>
          )}
          {!demoMode && (
            <span className="text-xs text-gray-400">Auto-refreshes every 30s</span>
          )}

          <Button
            variant={demoMode ? 'default' : 'outline'}
            size="sm"
            onClick={() => setDemoMode((d) => !d)}
            className="gap-2"
          >
            <FlaskConical className="h-4 w-4" />
            {demoMode ? 'Demo ON' : 'Demo'}
          </Button>

          {!demoMode && (
            <Button variant="outline" size="sm" onClick={() => refetch()} disabled={accId == null || isFetching}>
              <RefreshCw className={cn('h-4 w-4 mr-2', isFetching && 'animate-spin')} />
              Refresh
            </Button>
          )}
        </div>
      </div>

      {/* Demo banner */}
      {demoMode && (
        <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
          <FlaskConical className="h-4 w-4 shrink-0" />
          Demo mode — sample data for acc 7943 (policy, customer, member, asset). Toggle <strong>Demo ON</strong> to disable.
        </div>
      )}

      {/* No account selected prompt — only in live mode */}
      {!demoMode && accId == null && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-4 flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-amber-500 flex-shrink-0" />
          <p className="text-sm text-amber-800">
            Select an account from the filter bar above to view pipeline status.
          </p>
        </div>
      )}

      {/* Stats row */}
      {data && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
            <p className="text-sm font-medium text-gray-500">Total Files</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{formatNumber(data.totalFiles)}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
            <p className="text-sm font-medium text-gray-500">In Progress</p>
            <p className="text-2xl font-bold text-blue-700 mt-1 flex items-center gap-1.5">
              <Loader2 className="h-5 w-5 animate-spin" />
              {formatNumber(data.filesInProgress)}
            </p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
            <p className="text-sm font-medium text-gray-500">Done</p>
            <p className="text-2xl font-bold text-green-700 mt-1 flex items-center gap-1.5">
              <CheckCircle className="h-5 w-5" />
              {formatNumber(data.filesDone)}
            </p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
            <p className="text-sm font-medium text-gray-500">With Errors</p>
            <p className="text-2xl font-bold text-red-700 mt-1 flex items-center gap-1.5">
              <XCircle className="h-5 w-5" />
              {formatNumber(data.filesWithErrors)}
            </p>
          </div>
        </div>
      )}

      {/* Filters */}
      {(demoMode || accId != null) && (
        <div className="flex items-center gap-3">
          <div className="w-52">
            <Select
              options={STATUS_OPTIONS}
              value={statusFilter}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setStatusFilter(e.target.value)}
            />
          </div>
          <div className="w-40">
            <Select
              options={LIMIT_OPTIONS}
              value={limitFilter}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setLimitFilter(e.target.value)}
            />
          </div>
        </div>
      )}

      {/* Files table */}
      {(demoMode || accId != null) && (
        <section>
          {!demoMode && isLoading && <LoadingState message="Loading pipeline status..." />}
          {!demoMode && error && (
            <ErrorState
              error={error as Error}
              message="Failed to load pipeline data"
              onRetry={() => refetch()}
            />
          )}
          {data && (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 bg-gray-50">
                      <th className="pl-4 pr-2 py-3 w-8" />
                      <th className="px-3 py-3 text-left font-medium text-gray-600">File</th>
                      <th className="px-3 py-3 text-left font-medium text-gray-600">Category</th>
                      <th className="px-3 py-3 text-left font-medium text-gray-600">File Status</th>
                      <th className="px-3 py-3 text-right font-medium text-gray-600">Rows</th>
                      <th className="px-3 py-3 text-left font-medium text-gray-600">Created</th>
                      <th className="px-3 py-3 text-left font-medium text-gray-600">Pipeline Stages</th>
                      <th className="px-3 py-3 text-left font-medium text-gray-600">Overall</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {files.map((file) => (
                      <FileRow key={file.fileId} file={file} />
                    ))}
                    {files.length === 0 && (
                      <tr>
                        <td colSpan={8} className="px-4 py-12 text-center text-gray-400">
                          No files found{accId ? ` for account ${accId}` : ''}
                          {statusFilter !== 'ALL' && ` with status ${statusFilter}`}.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </section>
      )}
    </div>
  )
}
